import { Component, Input, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastService } from "@core/components/toasts/toasts.service";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormAbstact } from "app/views/pages/abstract/form-abstract";
import moment from "moment-business-days";
import { CompanyService } from "services/corporate/company.service";
import { PointTimeExceptionService } from "services/corporate/pointTimeException.service";
import { TimeShiftService } from "services/hiring/timeShift.service";
import { PointTimeExceptionModel } from "services/models/corporate/pointTimeException.model";
import { EmployeeModel } from "services/models/employee.model";
import { DayOfWeek } from "services/models/hiring/sequenceTimeShift.model";
import { TimeShiftModel } from "services/models/hiring/timeShift.model";
import { OptionModel } from "services/models/model";
import { FormValidations } from "utils/form-validation";
import { comparer, convertToUpperCase, isValidDateValue } from "utils/helps";
import "moment/locale/pt-br";
import { PointTimePeriodService } from "services/corporate/pointTimePeriod.service";
import { TimeOccurrenceParametersModel } from "services/models/timeOccurrenceParameters.model";

@Component({
  selector: "app-create-point-exception",
  templateUrl: "./create-point-exception.component.html",
  styleUrls: ["./create-point-exception.component.scss"],
})
export class CreatePointTimeExceptionModalComponent
  extends FormAbstact<PointTimeExceptionModel, PointTimeExceptionService>
  implements OnInit
{
  @Input() pointTimeException: PointTimeExceptionModel;
  @Input() employee: EmployeeModel = new EmployeeModel();
  @Input() parameters: TimeOccurrenceParametersModel = new TimeOccurrenceParametersModel();
  showEntryDepartureFields = true;
  entryDepartureFields = ["entry1", "departure1", "entry2", "departure2"];
  timeShifts: TimeShiftModel[] = [];
  timeShiftsOptions: OptionModel[] = [];
  typeOptions: OptionModel[] = [
    { value: "1", label: "TRABALHADO" },
    { value: "2", label: "DSR" },
  ];
  hasPeriods: boolean = false;
  isLoading: boolean = false;
  compareValues = comparer;
  convertToUpperCase = convertToUpperCase;

  constructor(
    private formBuilder: UntypedFormBuilder,
    public activeModal: NgbActiveModal,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private toasterService: ToastService,
    private _companyService: CompanyService,
    private _timeShiftService: TimeShiftService,
    private service: PointTimeExceptionService,
    private _pointTimePeriodService: PointTimePeriodService
  ) {
    super(service, activeRoute, router, toasterService);
    moment.locale("pt-br");
  }

  ngOnInit(): void {
    this.initializeForm();
    if (this.pointTimeException) {
      this.form.patchValue(this.pointTimeException);
      this.timeShiftOrDateChange();
    }
    this.getTimeShiftByCompany();
  }

  initializeForm() {
    const dateValidators = [Validators.required];

    if (!this.parameters.canCreateRetroativeExceptions) {
      dateValidators.push(
        FormValidations.minDateValidator(moment().format("YYYY-MM-DD"))
      );
    }

    this.form = this.formBuilder.group({
      date: ["", dateValidators],
      type: [1, [Validators.required]],
      timeShiftId: [],
      entry1: ["", [Validators.required]],
      departure1: ["", [Validators.required]],
      entry2: ["", [Validators.required]],
      departure2: ["", [Validators.required]],
      employeeId: [this.employee.id, [Validators.required]],
    });
  }

  getTimeShiftByCompany() {
    this._companyService
      .loadTimeShifts(this.employee.companyId)
      .subscribe((res) => {
        this.timeShifts = res;
        this.timeShiftsOptions = res.map((x) => x.toOption());
      });
  }

  timeShiftOrDateChange() {
    this.hasPeriods = false;
    if (!this.dateField.value) {
      this.hasPeriods = false;
      this.toasterService.showWarning("Selecione uma data!");
      return;
    }

    if (!this.hasPeriods && isValidDateValue(this.dateField.value)) {
      this._pointTimePeriodService
        .checkPeriodAvailableByCompany(
          this.employee.companyId,
          this.dateField.value
        )
        .subscribe((hasPeriods) => {
          this.hasPeriods = hasPeriods;
          if (!hasPeriods) {
            this.toasterService.showWarning(
              "Não há Períodos em aberto na data selecionada."
            );
            this.dateField.setValue("");
          }
        });
    }

    if (this.typeField.value == 2) return;
    if (!this.timeShiftField.value || !this.dateField.value) {
      this.clearEntryDepartureValues();
      return;
    }

    this.isLoading = true;
    this._timeShiftService
      .getSequencesByTimeShift(this.timeShiftField.value)
      .subscribe(
        (res) => {
          const selectedDay = moment(this.dateField.value)
            .format("dddd")
            .split("-")[0];
          const sequentialOfTheDay = res.data.filter(
            (x) => x.day === DayOfWeek[selectedDay]
          );

          if (sequentialOfTheDay.length > 0) {
            this.updateFormFields(sequentialOfTheDay[0]);
          } else {
            this.clearEntryDepartureValues();
          }

          this.isLoading = false;
        },
        () => (this.isLoading = false)
      );
  }

  typeChange() {
    if (this.typeField.value == 2) {
      this.showEntryDepartureFields = false;
      this.updateFormFields({
        entry1: "00:00:59",
        departure1: "00:00:59",
        entry2: "00:00:59",
        departure2: "00:00:59",
      });
    } else {
      this.showEntryDepartureFields = true;
      this.timeShiftField.setValue(null);
      this.clearEntryDepartureValues();
    }
  }

  updateFormFields(data: any) {
    for (const field of this.entryDepartureFields) {
      this.form.get(field).setValue(data[field]);
    }
  }

  clearEntryDepartureValues() {
    this.updateFormFields({
      entry1: null,
      departure1: null,
      entry2: null,
      departure2: null,
    });
  }

  onDismiss(): void {
    this.activeModal.close();
  }

  handleSubmit() {
    if (this.validaForm()) {
      this.object = this.object.updateModel(this.form?.value);
      this.convertToUpperCase(this.object);
      this.isLoading = true;
      this.service.save(this.object.id, this.object).subscribe(
        (x) => {
          this.toasterService.showSuccess(
            "Exceção de ponto criada com sucesso!"
          );
          this.isLoading = false;
          this.activeModal.close(true);
        },
        () => {
          this.isLoading = false;
        }
      );
    }
  }

  get typeField() {
    return this.form.get("type") as UntypedFormControl;
  }
  get dateField() {
    return this.form.get("date") as UntypedFormControl;
  }
  get timeShiftField() {
    return this.form.get("timeShiftId") as UntypedFormControl;
  }
}
